import { request } from '../../helpers/apiHelper'
import { displayNotification } from '../../actions/notificationsActions'
import {
  createAddressMutation,
  createProductDocsMutation,
  createTicketMutation,
  deleteAddressMutation,
  deleteBankAccountMutation,
  disableUserMutation,
  resendConfirmationEmailMutation,
  resetPasswordMutation,
  suspendFraudulentUserMutation,
  updateAddressMutation,
  updateUserMutation,
  userQuery,
  usersQuery,
  userTicketsQuery,
  userDealsQuery,
  vehicleDealsQuery,
} from './queries'

export const createProductDocs = (id) => (dispatch) => {
  return request(createProductDocsMutation, { id })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const { envelope } = response.data.data.createProductDocs
        dispatch({
          type: 'CREATE_PRODUCT_DOCS_SUCCESS',
          entity: envelope,
        })
        return envelope
      }
    })
    .catch((error) => {
      dispatch({ type: 'CREATE_PRODUCT_DOCS_FAILURE' })
      dispatch(
        displayNotification(
          `Failed to create product docs: ${error.message}`,
          'error'
        )
      )
      throw new Error(error)
    })
}

export const resendConfirmationEmail = (id) => (dispatch) => {
  return request(resendConfirmationEmailMutation, { id })
    .then(() => {
      dispatch(
        displayNotification('The email verification email has been resent')
      )
    })
    .catch(() => {
      dispatch(
        displayNotification(
          'Failed to resend email verification email',
          'error'
        )
      )
    })
}

export const sendResetPasswordEmail = (id) => (dispatch) => {
  return request(resetPasswordMutation, { id })
    .then(() => {
      dispatch(displayNotification('The password reset email has been sent'))
    })
    .catch(() => {
      dispatch(
        displayNotification('Failed to send password reset email', 'error')
      )
    })
}

export const deleteBankAccount = (id) => (dispatch) => {
  return request(deleteBankAccountMutation, { id })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        dispatch({
          type: 'UPDATE_USER_SUCCESS',
          entity: response.data.data.deleteBankAccount.user,
        })
        dispatch(displayNotification('Successfully deleted'))
      }
    })
    .catch((error) => {
      dispatch({
        type: 'UPDATE_USER_FAILURE',
      })
      dispatch(displayNotification('Failed to delete', 'error'))
      throw new Error(error)
    })
}

function formatBirthday(user) {
  const { birthday } = user
  if (birthday) {
    const parts = birthday.split('-')
    const humanBirthday = `${parts[1]}/${parts[2]}/${parts[0]}`
    return Object.assign(user, { birthday: humanBirthday })
  } else {
    return user
  }
}

export const fetchUser = (id) => (dispatch) => {
  return request(userQuery, { id })
    .then((response) => {
      const user = formatBirthday(response.data.data.user)
      dispatch({
        type: 'FETCH_USER_SUCCESS',
        entity: user,
      })
    })
    .catch(() => {
      dispatch({
        type: 'FETCH_USER_FAILURE',
      })
      dispatch(displayNotification('Failed to fetch User', 'error'))
    })
}

/**
 * Deals for the CallTools deals modal: every deal on the vehicle that was just
 * synced, whoever owns it, plus every deal this customer has on their other
 * vehicles.
 *
 * Two requests rather than one because the API ANDs its filters, so asking for
 * vehicleId and userId together would return the intersection. They are merged
 * here and deduped by id — a deal belonging to this customer ON this vehicle
 * comes back from both.
 *
 * They are also two separate documents, not one document called twice. GraphQL
 * validates the document, so naming both arguments in one query is rejected
 * outright by an API that has only vehicleId — which took the modal down
 * entirely instead of just losing the customer's other deals.
 *
 * Each call keeps whatever the other returned if it fails, so one bad response
 * does not empty the modal. Resolves to the deal list rather than dispatching
 * it: the deals belong to a vehicle and a customer, not to a user entity the
 * reducer could merge them into.
 */
export const fetchDealsForSync =
  ({ vehicleId, userId }) =>
  (dispatch) => {
    const load = (query, variables) =>
      request(query, variables)
        .then((response) => {
          if (response.data.errors) {
            throw new Error(response.data.errors[0].message)
          }

          return response.data.data.productPackages.items || []
        })
        .catch(() => {
          dispatch(displayNotification('Failed to fetch some deals', 'error'))

          return []
        })

    return Promise.all([
      vehicleId ? load(vehicleDealsQuery, { vehicleId }) : [],
      userId ? load(userDealsQuery, { userId }) : [],
    ]).then(([vehicleDeals, userDeals]) => {
      const byId = new Map()
      vehicleDeals.concat(userDeals).forEach((deal) => {
        if (deal?.id != null) byId.set(String(deal.id), deal)
      })

      // Newest first, matching what each call already sorted by, so the merge
      // does not interleave the two lists arbitrarily.
      return Array.from(byId.values()).sort((a, b) =>
        String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
      )
    })
  }

/**
 * Loads the user's Zendesk tickets on demand. Kept out of fetchUser because
 * resolving `tickets` makes the API call out to Zendesk twice, which would
 * otherwise happen on every profile view for a tab that is rarely opened.
 * The users byId reducer merges, so this only adds the `tickets` key.
 */
export const fetchUserTickets = (id) => (dispatch) => {
  return request(userTicketsQuery, { id })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      }
      const { user } = response.data.data
      dispatch({
        type: 'UPDATE_USER_SUCCESS',
        entity: { id: user.id, tickets: user.tickets || [] },
      })
    })
    .catch(() => {
      dispatch(displayNotification('Failed to fetch Zendesk tickets', 'error'))
    })
}

// `notificationDuration` lets one screen keep its messages up longer without
// changing them anywhere else. Only Edit Admin passes it today; every other
// caller keeps the shared default.
export const updateUser =
  (
    newState,
    { notificationDuration }: { notificationDuration?: number } = {}
  ) =>
  (dispatch) => {
    return request(updateUserMutation, { user: newState })
      .then((response) => {
        if (response.data?.data?.updateUser?.warning) {
          dispatch(
            displayNotification(
              `Warning: ${response.data?.data?.updateUser?.warning}`,
              'error',
              notificationDuration
            )
          )
        }
        if (response.data.errors) {
          throw new Error(response.data.errors[0].message)
        } else {
          const user = formatBirthday(response.data.data.updateUser.user)
          dispatch({
            type: 'UPDATE_USER_SUCCESS',
            entity: user,
          })
        }
      })
      .catch((error) => {
        dispatch({
          type: 'UPDATE_USER_FAILURE',
        })
        dispatch(
          displayNotification(
            `Failed to update User: ${error.message}`,
            'error',
            notificationDuration
          )
        )
        throw new Error(error)
      })
  }

export const fetchUsers = (varObject) => async (dispatch) => {
  try {
    let response = await request(usersQuery, varObject)
    dispatch({
      type: 'FETCH_USERS_SUCCESS',
      entities: response.data.data.users.items,
      meta: response.data.data.users.meta,
    })
  } catch {
    dispatch({
      type: 'FETCH_USERS_FAILURE',
    })
    dispatch(displayNotification('Failed to fetch Users', 'error', 10000))
  }
}

export const disableUser = (id) => (dispatch) => {
  return request(disableUserMutation, { DisableUserInput: { id } })
    .then((res) => {
      if (res.data.errors) {
        throw new Error(res.data.errors[0].message)
      } else {
        const user = res.data.data.disableUser.user
        dispatch({
          type: 'UPDATE_USER_SUCCESS',
          entity: user,
        })
      }
    })
    .catch((error) => {
      dispatch({
        type: 'UPDATE_USER_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const suspendFraudulentUser = (id) => (dispatch) => {
  return request(suspendFraudulentUserMutation, {
    SuspendFraudulentUserInput: { id },
  })
    .then((res) => {
      if (res.data.errors) {
        throw new Error(res.data.errors[0].message)
      } else {
        const user = res.data.data.suspendFraudulentUser.user
        dispatch({
          type: 'UPDATE_USER_SUCCESS',
          entity: user,
        })
      }
    })
    .catch((error) => {
      dispatch({
        type: 'UPDATE_USER_FAILURE',
      })
      dispatch(displayNotification(`${error.message}`, 'error'))
      throw new Error(error)
    })
}

export const createTicket = (varObject, user) => (dispatch) => {
  return request(createTicketMutation, { ticket: varObject })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        // user.tickets is absent until the Zendesk tab has loaded them.
        const tickets = [
          response.data.data.createTicket.ticket,
          ...(user.tickets || []),
        ]
        dispatch({
          type: 'UPDATE_USER_SUCCESS',
          entity: Object.assign({}, user, { tickets }),
        })
      }
    })
    .catch((error) => {
      dispatch({
        type: 'UPDATE_USER_FAILURE',
      })
      dispatch(
        displayNotification(
          `Failed to create ticket: ${error.message}`,
          'error'
        )
      )
      throw new Error(error)
    })
}

export const createAddress = (varObject, user) => (dispatch) => {
  return request(createAddressMutation, { address: varObject })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const { address, user: updatedUser } = response.data.data.createAddress
        const addresses = [address, ...user.addresses]
        dispatch({
          type: 'UPDATE_USER_SUCCESS',
          // addressNeeded comes back from the server: adding the first address
          // is what clears the "Address needed" warning and unblocks signing.
          entity: Object.assign({}, user, {
            addresses,
            addressNeeded: updatedUser?.addressNeeded ?? user.addressNeeded,
          }),
        })
      }
    })
    .catch((error) => {
      dispatch({ type: 'UPDATE_USER_FAILURE' })
      dispatch(
        displayNotification(
          `Failed to create address: ${error.message}`,
          'error'
        )
      )
      throw new Error(error)
    })
}

export const updateAddress = (varObject) => (dispatch) => {
  return request(updateAddressMutation, { address: varObject })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        const updatedUser = response.data.data.updateAddress.user
        dispatch({ type: 'UPDATE_USER_SUCCESS', entity: updatedUser })
      }
    })
    .catch((error) => {
      dispatch({ type: 'UPDATE_USER_FAILURE' })
      dispatch(
        displayNotification(
          `Failed to update address: ${error.message}`,
          'error'
        )
      )
      throw new Error(error)
    })
}

export const deleteAddress = (id) => (dispatch) => {
  return request(deleteAddressMutation, { id })
    .then((response) => {
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      } else {
        dispatch({
          type: 'UPDATE_USER_SUCCESS',
          entity: response.data.data.deleteAddress.user,
        })
        dispatch(displayNotification('Successfully deleted'))
      }
    })
    .catch((error) => {
      dispatch({ type: 'UPDATE_USER_FAILURE' })
      dispatch(displayNotification('Failed to delete', 'error'))
      throw new Error(error)
    })
}
